import { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { getResendEmailInfo, resendEmail, type ResendEmailInfo } from "../../api/tools.ts";
import fp from "../../styles/formPage.module.scss";

interface ResendEmailModalProps {
  open: boolean;
  emailLogId: number;
  transactionId: number;
  /** Pre-fill recipient with the customer's email from the row, used until the
   *  server's CustomerEmail comes back. */
  customerEmail?: string;
  onClose: () => void;
}

/** Mirrors WebAdmin2's ResendEmailPopup: pre-fills recipient + sender from
 *  WebGetResendEmailInfo, lets the operator override, then calls
 *  WebResendEmail. Subject defaults to "<Resend on Request>". */
export function ResendEmailModal({ open, emailLogId, transactionId, customerEmail, onClose }: ResendEmailModalProps) {
  const { toast } = useToast();
  const [info, setInfo] = useState<ResendEmailInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [subject, setSubject] = useState("<Resend on Request>");
  const [senderName, setSenderName] = useState("");
  const [senderId, setSenderId] = useState<number>(-1);

  // Stable refs so the load effect's dep array stays primitives — otherwise
  // `toast` (object from useToast) and `customerEmail` churn would re-fire
  // the WebGetResendEmailInfo HTTP request on every parent render.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const customerEmailRef = useRef(customerEmail);
  customerEmailRef.current = customerEmail;
  // Dedupe in-flight fetch so StrictMode's dev double-mount doesn't fire two
  // calls. Cleared on completion so reopens after a finish refetch.
  const inFlightRef = useRef<{ id: number; promise: ReturnType<typeof getResendEmailInfo> } | null>(null);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setInfo(null);
    setSendTo(customerEmailRef.current ?? "");
    setSubject("<Resend on Request>");
    setSenderName("");
    setSenderId(-1);
    let cancelled = false;
    let promise: ReturnType<typeof getResendEmailInfo>;
    if (inFlightRef.current?.id === transactionId) {
      promise = inFlightRef.current.promise;
    } else {
      promise = getResendEmailInfo(transactionId);
      inFlightRef.current = { id: transactionId, promise };
    }
    promise
      .then((res) => {
        if (cancelled) return;
        if (res.bError || !res.oData) {
          toastRef.current(res.sMessage || "Failed to load resend info", "error");
          return;
        }
        const d = res.oData;
        setInfo(d);
        setSendTo(d.CustomerEmail || customerEmailRef.current || "");
        setSenderName(d.DefaultSenderName ?? "");
        const def = d.ValidatedEmailSenders?.find(
          (s) => s.Email.toUpperCase() === (d.DefaultSenderEmail ?? "").toUpperCase(),
        );
        if (def) setSenderId(def.AddressID);
        else if (d.ValidatedEmailSenders?.length) setSenderId(d.ValidatedEmailSenders[0]!.AddressID);
      })
      .catch((err) => { if (!cancelled) toastRef.current(String(err), "error"); })
      .finally(() => {
        if (!cancelled) setLoading(false);
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      });
    return () => { cancelled = true; };
  }, [open, transactionId]);

  const handleResend = async () => {
    if (!sendTo.trim()) {
      toast("Recipient address is required", "error");
      return;
    }
    if (senderId < 0) {
      toast("Pick a sender address", "error");
      return;
    }
    setSending(true);
    try {
      const res = await resendEmail(emailLogId, transactionId, sendTo.trim(), subject, senderName, senderId);
      if (res.bError || !res.oData) {
        toast(res.sMessage || "Could not resend email", "error");
        return;
      }
      toast(`Email #${emailLogId} resent to ${sendTo.trim()}`, "success");
      onClose();
    } catch (err) {
      toast(String(err), "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={`Resend email #${emailLogId}`} onClose={onClose} />
      <ModalBody>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            <div className={fp.fieldRow}>
              <label>Send to</label>
              <input
                className={fp.fieldInput}
                type="email"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
              />
            </div>
            <div className={fp.fieldRow}>
              <label>Subject</label>
              <input
                className={fp.fieldInput}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className={fp.fieldRow}>
              <label>Sender name</label>
              <input
                className={fp.fieldInput}
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
            </div>
            <div className={fp.fieldRow}>
              <label>Sender address</label>
              <select
                className={fp.fieldSelect}
                value={senderId}
                onChange={(e) => setSenderId(Number(e.target.value))}
              >
                {info?.ValidatedEmailSenders?.length ? (
                  info.ValidatedEmailSenders.map((s) => (
                    <option key={s.AddressID} value={s.AddressID}>{s.Email}</option>
                  ))
                ) : (
                  <option value={-1}>(no validated senders)</option>
                )}
              </select>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button className={fp.secondaryBtn} onClick={onClose} disabled={sending}>
          Cancel
        </button>
        <button className={fp.primaryBtn} onClick={handleResend} disabled={sending || loading}>
          {sending ? "Sending…" : "Resend email"}
        </button>
      </ModalFooter>
    </Modal>
  );
}
